import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, MailOpen, Save, Users } from "lucide-react";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TextInput = (props) => (
  <input {...props} className={`hk-input h-10 w-full ${props.className || ""}`} />
);

export default function AdminSubscribersPage() {
  const pw = useAdminAuthed();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ enabled: false, heading: "", subtext: "", delay_seconds: 5 });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/subscribers`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/settings/popup`, { headers: adminHeaders() }),
      ]);
      setSubs(s.data.subscribers || []);
      const d = c.data || {};
      setCfg({
        enabled: !!d.enabled,
        heading: d.heading || "",
        subtext: d.subtext || "",
        delay_seconds: Number(d.delay_seconds) || 5,
      });
    } catch {
      toast.error("Failed to load subscribers");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (pw) load(); }, [pw, load]);

  const saveCfg = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/popup`, cfg, { headers: adminHeaders() });
      toast.success("Popup settings saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: adminHeaders() });
      setSubs((s) => s.filter((x) => x.id !== id));
      toast.success("Subscriber removed");
    } catch {
      toast.error("Delete failed");
    } finally { setDeleting(null); }
  };

  return (
    <AdminLayout title="Subscribers">
      <div className="space-y-6" data-testid="admin-subscribers-page">
        {/* Popup settings */}
        <div className="hk-card p-6" data-testid="popup-settings-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MailOpen size={18} className="text-[var(--hk-green,#3a7d44)]" />
              <h2 className="text-lg font-semibold">Homepage Email Popup</h2>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="popup-enabled-toggle">
              <input type="checkbox" checked={cfg.enabled}
                onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))}
                className="w-4 h-4 accent-[var(--hk-green,#3a7d44)]" />
              Popup {cfg.enabled ? "ON" : "OFF"}
            </label>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-[var(--hk-ink-3)] mb-1">Heading</label>
              <TextInput value={cfg.heading} placeholder="Join the HerbKranti family"
                onChange={(e) => setCfg((c) => ({ ...c, heading: e.target.value }))}
                data-testid="popup-heading-input" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-[var(--hk-ink-3)] mb-1">Subtext</label>
              <TextInput value={cfg.subtext} placeholder="Get wellness tips, new harvest updates and member-only offers."
                onChange={(e) => setCfg((c) => ({ ...c, subtext: e.target.value }))}
                data-testid="popup-subtext-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--hk-ink-3)] mb-1">Show after (seconds)</label>
              <TextInput type="number" min="1" value={cfg.delay_seconds}
                onChange={(e) => setCfg((c) => ({ ...c, delay_seconds: Number(e.target.value) || 5 }))}
                data-testid="popup-delay-input" />
            </div>
          </div>
          <button onClick={saveCfg} disabled={saving} className="hk-btn-primary h-10 px-5 mt-4 inline-flex items-center gap-2"
            data-testid="popup-settings-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save popup settings
          </button>
        </div>

        {/* Subscribers list */}
        <div className="hk-card p-6" data-testid="subscribers-list-card">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-[var(--hk-green,#3a7d44)]" />
            <h2 className="text-lg font-semibold">Captured Emails</h2>
            <span className="text-xs text-[var(--hk-ink-3)]" data-testid="subscribers-count">({subs.length})</span>
          </div>
          {loading ? (
            <div className="py-8 text-center text-[var(--hk-ink-3)]"><Loader2 className="animate-spin inline" size={20} /></div>
          ) : subs.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--hk-ink-3)]" data-testid="subscribers-empty">
              No subscribers yet — turn the popup ON and they will appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subscribers-table">
                <thead>
                  <tr className="text-left text-xs text-[var(--hk-ink-3)] border-b border-black/10">
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Mobile</th>
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">Subscribed on</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-b border-black/5" data-testid={`subscriber-row-${s.id}`}>
                      <td className="py-2.5 pr-4 font-medium">{s.email}</td>
                      <td className="py-2.5 pr-4" data-testid={`subscriber-phone-${s.id}`}>
                        {s.phone ? <a href={`tel:${s.phone}`} className="hover:text-[var(--hk-orange-deep)]">{s.phone}</a> : <span className="text-[var(--hk-ink-3)]">—</span>}
                      </td>
                      <td className="py-2.5 pr-4 text-[var(--hk-ink-3)]">{s.source}</td>
                      <td className="py-2.5 pr-4 text-[var(--hk-ink-3)]">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="py-2.5 text-right">
                        <button onClick={() => remove(s.id)} disabled={deleting === s.id}
                          className="hk-btn-ghost h-8 px-3 text-xs inline-flex items-center gap-1"
                          data-testid={`subscriber-delete-${s.id}`}>
                          {deleting === s.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
